import { useEffect, useRef } from 'react';
import { toastConfirm } from '../common/toast';

const STORAGE_KEY = 'pg_payment_return_pending';

export const PG_PAY_CONFIRM_MESSAGE =
  'This will open an external payment page in a new window. After you complete the payment, refresh this page to see the updated status.';

/** Call right before opening a payment gateway URL. */
export function markPaymentRedirect() {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (_) {
    /* ignore */
  }
}

/**
 * Ask the guest to confirm external payment.
 * On confirm the toast closes; caller should then open the payment URL.
 */
export async function confirmExternalPayment() {
  return toastConfirm(true, PG_PAY_CONFIRM_MESSAGE);
}

/** Open a payment gateway URL in a new window and mark return-for-refresh. */
export function openExternalPayment(paymentUrl) {
  if (!paymentUrl) return false;
  markPaymentRedirect();
  window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  return true;
}

function consumePaymentReturnPending() {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

/**
 * Refresh PG screens when the user returns from a payment gateway
 * (full remount, tab focus, or bfcache back-navigation).
 */
export function useRefreshOnPaymentReturn(refreshFn) {
  const refreshRef = useRef(refreshFn);
  refreshRef.current = refreshFn;

  useEffect(() => {
    const tryRefresh = () => {
      if (typeof refreshRef.current === 'function' && consumePaymentReturnPending()) {
        refreshRef.current();
      }
    };

    tryRefresh();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryRefresh();
    };

    window.addEventListener('pageshow', tryRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', tryRefresh);

    return () => {
      window.removeEventListener('pageshow', tryRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', tryRefresh);
    };
  }, []);
}
